import { useEffect, useState } from "react";
import Modal from "@shared/v2/Modal";
import Typography from "@shared/v2/Typography";
import { XmarkSolidV6 } from "@shared/v2/Icomoon";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import TextInput from "@shared/v2/TextInput";
import Loading from "../../../../Goals/components/Loader";
import { AccountProduct } from "../../../../Support/Account/AddOnsSupport/types";

interface FormData {
  churnCode: string;
  churnCodeFeedback: string;
}

interface PhoneSystemCancelModalProps {
  accountProduct?: AccountProduct;
  loading?: boolean;
  onClose: () => void;
  onCancelPlan: (
    accountProductId: string | number,
    cancelForm: { status: string; details: { churnCode: string; churnCodeFeedback: string } },
  ) => void;
  open: boolean;
  trackingNumbers: Array<unknown>;
}

const CANCEL_REASONS = [
  "Our team isn't getting a lot of use out of Phone System",
  "The product is not intuitive to use",
  "I'm cancelling due to financial constraints",
  "Phone System is missing features that I need",
  "Phone System is too difficult to set up",
  "I’d like to use another service for call tracking",
  "I haven’t received enough calls or leads",
  "I had technical issues with this product",
].map((reason) => ({ label: reason, value: reason }));

const PhoneSystemCancelModal: React.FC<PhoneSystemCancelModalProps> = ({
  accountProduct,
  loading = false,
  onCancelPlan,
  onClose,
  open,
  trackingNumbers,
}) => {
  const [step, setStep] = useState(1);
  const [churnCode, setChurnCode] = useState<(typeof CANCEL_REASONS)[0] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setStep(1);
      setChurnCode(null);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    if (step === 1) {
      setStep(2);
    } else {
      const details = Object.fromEntries(new FormData(e.currentTarget).entries()) as unknown as FormData;
      if (!details.churnCode) {
        setErrors({ churnCode: "Please select a reason for cancelling" });
        return;
      }
      onCancelPlan(accountProduct?.id ?? "", { status: "canceled", details });
    }
  };

  return (
    <Modal
      onHide={onClose}
      show={open}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col"
    >
      <form className="tw-flex tw-flex-col tw-gap-[32px] tw-overflow-hidden" onSubmit={handleSubmit}>
        {/* @ts-expect-error props */}
        <Modal.Header
          title={step === 1 ? "Are you sure?" : "Confirm Phone System Cancellation"}
          closeButton
        />

        <Modal.Body className="tw-flex-1 tw-overflow-y-auto tw-flex tw-flex-col tw-gap-[24px] tw-pb-[16px]">
          {loading && (
            <div className="tw-flex tw-justify-center tw-py-[24px]">
              <Loading />
            </div>
          )}
          {step === 1 && (
            <div className="tw-flex tw-flex-col tw-gap-[8px]">
              {/* @ts-expect-error props */}
              <Typography>
                Your tracking numbers help your team capture every call and text from your marketing
                campaigns, ensuring you never miss out on a lead that turns into a transaction.{" "}
              </Typography>
              {/* @ts-expect-error props */}
              <Typography>If you cancel, you’ll lose access to...</Typography>
              {/* @ts-expect-error props */}
              <Typography className="tw-flex tw-items-center tw-gap-[4px]">
                <XmarkSolidV6 size="l" className="tw-text-semantic-red-100" /> Your{" "}
                {trackingNumbers.length ?? ""} tracking numbers
                {/*, which has brought in 52 leads into your CRM*/}
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="tw-flex tw-items-center tw-gap-[4px]">
                <XmarkSolidV6 size="l" className="tw-text-semantic-red-100" /> Automatic lead creation and
                source attribution
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="tw-flex tw-items-center tw-gap-[4px]">
                <XmarkSolidV6 size="l" className="tw-text-semantic-red-100" /> Campaign performance insights
              </Typography>
            </div>
          )}

          {step === 2 && (
            <>
              {/* @ts-expect-error props */}
              <Typography>
                <b>Your phone system service will end at the end of the current billing period.</b> You and
                your agents can continue to receive calls from tracking numbers and access the Phone System
                call logs until then.
              </Typography>
              {/* @ts-expect-error props */}
              <Typography className="!tw-italic">
                All cancellations must be done 7 days before your bill date, or you will be charged for the
                following month.
              </Typography>
              {/* @ts-expect-error props */}
              <Typography>
                <b>At the end of the current billing period...</b>
                <ul className="tw-pl-[24px]">
                  <li>
                    Any overages incurred after cancellation but before service ends will be added to your
                    final invoice at the end of this current billing period.
                  </li>
                  <li>
                    You and your team will still have access to previous Phone System related timeline events
                    and activity feed notifications across your CRM.
                  </li>
                  <li>
                    However, you will no longer have access to the Phone System pages, including Reporting,
                    Call Logs, and Settings.
                  </li>
                  <li>
                    We will maintain call recordings and transcriptions on timeline events for up to 90 days.
                    After 90 days, they may be removed.{" "}
                    <b>If you need a number ported, you must do so before your service ends.</b>
                  </li>
                </ul>
              </Typography>
              <Dropdown
                // @ts-expect-error props
                label="Reason for Cancelling"
                isRequired
                name="churnCode"
                helperText="To help us improve Phone System, please share why you're cancelling"
                error={errors.churnCode}
                options={CANCEL_REASONS}
                value={churnCode}
                onChange={setChurnCode}
                menuShouldComeToFront
              />
              <TextInput
                // @ts-expect-error props
                label="Additional Feedback"
                name="churnCodeFeedback"
                multiline
                rows={4}
                placeholder="How can we make Phone System better?"
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
          {/* @ts-expect-error props */}
          <Button size="medium" schema={step === 1 ? "secondary" : "tertiary"} onClick={onClose}>
            {step === 1 ? "Stay on Phone System" : "Keep My Subscription"}
          </Button>
          {/* @ts-expect-error props */}
          <Button type="submit" size="medium" schema={step === 1 ? "tertiary" : "warning"}>
            {step === 1 ? "Continue to Cancel" : "Cancel Phone System"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default PhoneSystemCancelModal;
